import {useQuery} from '@tanstack/react-query';
import {useConfig} from '@lore/config';

/**
 * @typedef {Object} EngagementItem
 * @property {number} id
 * @property {string} name
 * @property {string} description
 */

/**
 * @typedef {Object} EngagementSection
 * @property {number} total
 * @property {EngagementItem[]} data
 */

/**
 * @typedef {Object} SiteEngagements
 * @property {number} siteId
 * @property {EngagementSection} projects
 * @property {EngagementSection} events
 * @property {EngagementSection} offers
 * @property {EngagementSection} evaluations
 */

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {EngagementItem}
 */
export const _EngagementItem = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {EngagementSection}
 */
export const _EngagementSection = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {SiteEngagements}
 */
export const _SiteEngagements = {};

const STALE_TIME = 15 * 60 * 1000; // 15 minutes

/**
 * Custom hook to fetch site engagements from the core API.
 *
 * @param {number|string} siteId - The ID of the site to fetch engagements for.
 * @param {Object} options - Configuration options like 'enabled' to control the query.
 * @returns {import('@tanstack/react-query').UseQueryResult<SiteEngagements>} React Query result object.
 */
export default function useCoreSiteEngagements(siteId, {enabled = true} = {}) {
  const config = useConfig();
  const {apiRoot, headers} = config.connections.default;

  const query = useQuery({
    queryKey: ['sites', siteId, 'engagements'],

    queryFn: () => {
      const url = `${apiRoot}/core/sites/${siteId}/engagements`;

      return fetch(url, {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch engagements for site ${siteId}`);
        }
        return res.json();
      });
    },

    staleTime: STALE_TIME,
    enabled: siteId != null && !isNaN(siteId) && enabled,
  });

  return {
    ...query,
  };
}
